/**
 * Producten en monitoring (US-006, US-030, aanvulling op US-004).
 *
 * Twee dingen op één scherm, omdat ze bij elkaar horen:
 *  - welke producten er zijn en welke daarvan meedraaien in de dagelijkse controle;
 *  - een keuzelijst uit de XML-feed, zodat een product niet eerst in Excel hoeft te staan.
 *
 * Een product uit de feed komt binnen als concept. Levert de feed een lengte- en kleurveld, dan
 * worden die overgenomen; anders wordt de lengte uit de productnaam geraden en is dat zichtbaar
 * als zodanig. Zonder kloppende lengte kan de variantcontrole niets vergelijken (US-018), dus
 * activeren kan pas nadat de lengte is bevestigd.
 */

import { formatEuro, PRODUCT_STATUS_LABELS, type ProductStatus } from '@prijsmonitor/shared';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { SharedProductLinker } from './SharedProductLinker.js';
import { ApiError, api } from '../api/client.js';
import { Button, Card, Chip, Notice, ui } from '../components/ui/index.js';
import styles from './ProductsScreen.module.css';

interface Product {
  id: string;
  name: string;
  ownSku: string;
  lengthMm: number | null;
  lengthLabel: string | null;
  color: string | null;
  colorCritical: boolean;
  foil: string | null;
  foilCritical: boolean;
  ean: string | null;
  manufacturerSku: string | null;
  ownShopUrl: string | null;
  /** Correctie op de feedprijs (US-008): leeg laat de feedprijs ongewijzigd. */
  nettoPercentage: number | null;
  status: ProductStatus;
  sourceCount: number;
  gedeeld: boolean;
}

interface CatalogEntry {
  sku: string;
  name: string | null;
  priceCents: number;
  lengteSuggestie: string | null;
  lengteUitFeed: boolean;
  kleur: string | null;
  /** De eigen productpagina uit de feed (`g:link`); wordt bij het aanmaken de terugvalbron. */
  webshopUrl: string | null;
  productId: string | null;
  productStatus: ProductStatus | null;
}

interface Catalog {
  entries: CatalogEntry[];
  totalInFeed: number;
  problems: number;
}

const STATUS_TONE: Record<ProductStatus, 'success' | 'warn' | 'muted'> = {
  ACTIVE: 'success',
  DRAFT: 'warn',
  ARCHIVED: 'muted',
};

export function ProductsScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const [koppelen, setKoppelen] = useState(false);

  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /** Welke status er in de lijst staat; 'ALLES' toont alles. */
  const [filter, setFilter] = useState<ProductStatus | 'ALLES'>('ALLES');
  /** Aangevinkte producten voor de bulkacties. */
  const [selectie, setSelectie] = useState<Set<string>>(new Set());
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [zoek, setZoek] = useState('');
  const [gekozen, setGekozen] = useState<Map<string, string>>(new Map());

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ products: Product[] }>('/api/products');
      setProducts(data.products);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'De producten konden niet worden geladen.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(key: string, actie: () => Promise<string | void>): Promise<void> {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const melding = await actie();
      if (typeof melding === 'string') setNotice(melding);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'De actie is niet gelukt.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Slaat één gewijzigd veld op. De PATCH verwacht het hele product, dus de overige velden
   * gaan ongewijzigd mee terug.
   */
  async function bewaar(
    product: Product,
    wijziging: Partial<Product>,
    melding: string,
  ): Promise<void> {
    await run(`bewerk-${product.id}`, async () => {
      await api.patch(`/api/products/${product.id}`, {
        name: product.name,
        ownSku: product.ownSku,
        lengthLabel: product.lengthLabel,
        color: product.color,
        colorCritical: product.colorCritical,
        foil: product.foil,
        foilCritical: product.foilCritical,
        ean: product.ean,
        manufacturerSku: product.manufacturerSku,
        ownShopUrl: product.ownShopUrl,
        nettoPercentage: product.nettoPercentage,
        ...wijziging,
      });
      return melding;
    });
  }

  /** Voert één bulkactie uit op de aangevinkte producten. */
  async function bulk(
    actie: 'activeren' | 'archiveren' | 'herstellen' | 'verwijderen',
  ): Promise<void> {
    const ids = [...selectie];
    if (ids.length === 0) return;

    if (actie === 'verwijderen') {
      const bevestigd = window.confirm(
        `${ids.length} product(en) definitief verwijderen?\n\n` +
          'Prijsbronnen, dagprijzen en bewijsmateriaal verdwijnen mee. Dit kan niet ongedaan ' +
          'worden gemaakt. Wilt u de historie bewaren, gebruik dan Stoppen in plaats hiervan.',
      );
      if (!bevestigd) return;
    }

    await run(`bulk-${actie}`, async () => {
      const resultaat = await api.post<{
        gelukt: number;
        mislukt: Array<{ id: string; reden: string }>;
        melding: string;
      }>('/api/products/bulk', { actie, ids });

      setSelectie(new Set());
      // De redenen erbij: meestal een product zonder lengte dat niet kan worden geactiveerd.
      const eerste = resultaat.mislukt[0];
      return eerste ? `${resultaat.melding} Bijvoorbeeld: ${eerste.reden}` : resultaat.melding;
    });
  }

  async function haalCatalogus(): Promise<void> {
    await run('catalogus', async () => {
      setCatalog(await api.get<Catalog>('/api/feed/catalogus'));
    });
  }

  async function maakAan(): Promise<void> {
    await run('aanmaken', async () => {
      const resultaat = await api.post<{
        aangemaakt: number;
        overgeslagen: Array<{ sku: string; reden: string }>;
      }>('/api/feed/producten', {
        producten: [...gekozen].map(([sku, lengthLabel]) => ({ sku, lengthLabel })),
      });

      setGekozen(new Map());
      setCatalog(await api.get<Catalog>('/api/feed/catalogus'));

      const overgeslagen = resultaat.overgeslagen.length;
      return overgeslagen === 0
        ? `${resultaat.aangemaakt} product(en) aangemaakt als concept.`
        : `${resultaat.aangemaakt} aangemaakt, ${overgeslagen} overgeslagen: ` +
            resultaat.overgeslagen.map((o) => `${o.sku} (${o.reden})`).join(', ');
    });
  }

  const aantallen = {
    ALLES: products.length,
    ACTIVE: products.filter((p) => p.status === 'ACTIVE').length,
    DRAFT: products.filter((p) => p.status === 'DRAFT').length,
    ARCHIVED: products.filter((p) => p.status === 'ARCHIVED').length,
  };
  const zichtbareProducten =
    filter === 'ALLES' ? products : products.filter((p) => p.status === filter);

  const zichtbaar = (catalog?.entries ?? []).filter((entry) => {
    if (!zoek.trim()) return true;
    const term = zoek.trim().toLowerCase();
    return (
      entry.sku.toLowerCase().includes(term) || (entry.name ?? '').toLowerCase().includes(term)
    );
  });

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}
      {notice && <Notice tone="success">{notice}</Notice>}

      {koppelen && (
        <SharedProductLinker
          onClose={() => setKoppelen(false)}
          onLinked={async (melding) => {
            setKoppelen(false);
            setNotice(melding);
            await load();
            if (catalog) setCatalog(await api.get<Catalog>('/api/feed/catalogus'));
          }}
        />
      )}
      <Card
        actions={
          <Button variant="secondary" onClick={() => setKoppelen(true)}>
            Bestaand gemonitord product koppelen
          </Button>
        }
        title="Producten"
        description="Alleen actieve producten draaien mee in de dagelijkse controle. Een concept telt niet mee zolang de lengte ontbreekt."
      >
        {/* Tegelijk teller en filter: de aantallen zeggen niets als je er niet naartoe kunt. */}
        <div className={styles.filters} role="tablist" aria-label="Filter op status">
          {(
            [
              { key: 'ACTIVE' as const, label: 'Actief gemonitord' },
              { key: 'DRAFT' as const, label: 'Concept' },
              { key: 'ARCHIVED' as const, label: 'Gearchiveerd' },
              { key: 'ALLES' as const, label: 'Alles' },
            ] satisfies Array<{ key: ProductStatus | 'ALLES'; label: string }>
          ).map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={filter === item.key}
              className={filter === item.key ? styles.filterActief : styles.filter}
              onClick={() => setFilter(item.key)}
            >
              <span className={styles.filterAantal}>{aantallen[item.key]}</span>
              {item.label}
            </button>
          ))}
        </div>

        {selectie.size > 0 && (
          <div className={styles.bulkBalk}>
            <strong>{selectie.size} geselecteerd</strong>
            {/* Uitgeschreven, want dezelfde woorden staan ook per rij; zo is duidelijk dat dit
                op de selectie werkt. */}
            <Button
              variant="secondary"
              busy={busy === 'bulk-activeren'}
              onClick={() => void bulk('activeren')}
            >
              Selectie monitoren
            </Button>
            <Button busy={busy === 'bulk-archiveren'} onClick={() => void bulk('archiveren')}>
              Selectie stoppen
            </Button>
            <Button busy={busy === 'bulk-herstellen'} onClick={() => void bulk('herstellen')}>
              Selectie terugzetten
            </Button>
            <span className={styles.bulkSpacer} />
            <Button
              variant="danger"
              busy={busy === 'bulk-verwijderen'}
              onClick={() => void bulk('verwijderen')}
            >
              Selectie verwijderen
            </Button>
            <Button onClick={() => setSelectie(new Set())}>Selectie wissen</Button>
          </div>
        )}

        {products.length === 0 ? (
          <p className={ui.empty}>
            Er zijn nog geen producten. Kies ze hieronder uit de XML-feed, of gebruik de
            Excel-import.
          </p>
        ) : (
          <div className={ui.tableWrap} style={{ marginTop: 'var(--space-12)' }}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th className={styles.checkCol}>
                    <input
                      type="checkbox"
                      aria-label="Alle zichtbare producten selecteren"
                      checked={
                        zichtbareProducten.length > 0 &&
                        zichtbareProducten.every((p) => selectie.has(p.id))
                      }
                      onChange={(e) =>
                        setSelectie(
                          e.target.checked
                            ? new Set(zichtbareProducten.map((p) => p.id))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                  <th>Product</th>
                  <th>Artikelnummer</th>
                  <th>Lengte</th>
                  <th>Kleur</th>
                  <th className={ui.numeric}>Nettocorrectie</th>
                  <th className={ui.numeric}>Bronnen</th>
                  <th>Status</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {zichtbareProducten.map((product) => (
                  <tr
                    key={product.id}
                    className={selectie.has(product.id) ? styles.gekozenRij : undefined}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectie.has(product.id)}
                        aria-label={`${product.name} selecteren`}
                        onChange={(e) =>
                          setSelectie((huidig) => {
                            const next = new Set(huidig);
                            if (e.target.checked) next.add(product.id);
                            else next.delete(product.id);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => void navigate(`/product/${product.id}`)}
                      >
                        {product.name}
                      </button>
                      {product.gedeeld && (
                        <div>
                          <Chip tone="muted">Gedeelde monitoring</Chip>
                        </div>
                      )}
                    </td>
                    <td>{product.ownSku}</td>
                    <td>
                      <input
                        className={styles.lengthInput}
                        defaultValue={product.lengthLabel ?? ''}
                        placeholder="bv. 600 cm"
                        aria-label={`Lengte van ${product.name}`}
                        disabled={busy === `bewerk-${product.id}`}
                        onBlur={(e) => {
                          const waarde = e.target.value.trim();
                          if ((product.lengthLabel ?? '') === waarde) return;
                          void bewaar(
                            product,
                            { lengthLabel: waarde || null },
                            'Lengte opgeslagen.',
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                        }}
                      />
                    </td>
                    <td>
                      <div className={styles.kleurCel}>
                        <input
                          className={styles.kleurInput}
                          defaultValue={product.color ?? ''}
                          placeholder="—"
                          aria-label={`Kleur van ${product.name}`}
                          disabled={busy === `bewerk-${product.id}`}
                          onBlur={(e) => {
                            const waarde = e.target.value.trim();
                            if ((product.color ?? '') === waarde) return;
                            void bewaar(product, { color: waarde || null }, 'Kleur opgeslagen.');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                        <label className={styles.kritisch}>
                          <input
                            type="checkbox"
                            checked={product.colorCritical}
                            disabled={!product.color || busy === `bewerk-${product.id}`}
                            onChange={(e) =>
                              void bewaar(
                                product,
                                { colorCritical: e.target.checked },
                                e.target.checked
                                  ? 'Kleur telt nu mee bij de variantcontrole.'
                                  : 'Kleur telt niet meer mee bij de variantcontrole.',
                              )
                            }
                          />
                          kritisch
                        </label>
                      </div>
                    </td>
                    <td className={ui.numeric}>
                      <input
                        className={styles.lengthInput}
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        defaultValue={product.nettoPercentage ?? ''}
                        placeholder="—"
                        aria-label={`Nettocorrectie van ${product.name}`}
                        title="Percentage waarmee de brutoprijs uit de feed wordt verlaagd tot de werkelijke nettoprijs. Leeg = feedprijs blijft ongewijzigd."
                        disabled={busy === `bewerk-${product.id}`}
                        onBlur={(e) => {
                          const ruw = e.target.value.trim();
                          const waarde = ruw === '' ? null : Number(ruw);
                          if ((product.nettoPercentage ?? null) === waarde) return;
                          void bewaar(
                            product,
                            { nettoPercentage: waarde },
                            waarde === null
                              ? 'Nettocorrectie verwijderd; de feedprijs wordt weer ongewijzigd gebruikt.'
                              : 'Nettocorrectie opgeslagen.',
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                        }}
                      />
                    </td>
                    <td className={ui.numeric}>{product.sourceCount}</td>
                    <td>
                      <Chip tone={STATUS_TONE[product.status]}>
                        {PRODUCT_STATUS_LABELS[product.status]}
                      </Chip>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {product.gedeeld && (
                          <Button
                            disabled={busy !== null}
                            onClick={() => {
                              if (
                                !window.confirm(
                                  'Deze productkoppeling losmaken? Het product en de historie blijven behouden. Concurrentbronnen worden voortaan zelfstandig beheerd en gemeten.',
                                )
                              )
                                return;
                              void run(`ontkoppelen-${product.id}`, async () => {
                                const resultaat = await api.post<{ melding: string }>(
                                  `/api/monitoring/${product.id}/ontkoppelen`,
                                  { bevestigd: true },
                                );
                                return resultaat.melding;
                              });
                            }}
                          >
                            Zelfstandig beheren
                          </Button>
                        )}
                        {product.status === 'DRAFT' && (
                          <Button
                            // Bewust niet de gele CTA: die is in de huisstijl voor één
                            // primaire actie per scherm, en dat is hier het aanmaken.
                            variant="secondary"
                            busy={busy === `activeren-${product.id}`}
                            disabled={product.lengthMm === null}
                            {...(product.lengthMm === null
                              ? { title: 'Vul eerst de lengte in.' }
                              : {})}
                            onClick={() =>
                              void run(`activeren-${product.id}`, () =>
                                api
                                  .post(`/api/products/${product.id}/activeren`)
                                  .then(() => 'Product wordt vanaf nu gemonitord.'),
                              )
                            }
                          >
                            Monitoren
                          </Button>
                        )}
                        {product.status === 'ACTIVE' && (
                          <Button
                            busy={busy === `archiveren-${product.id}`}
                            onClick={() =>
                              void run(`archiveren-${product.id}`, () =>
                                api
                                  .post(`/api/products/${product.id}/archiveren`)
                                  .then(() => 'Product gearchiveerd. De historie blijft bewaard.'),
                              )
                            }
                          >
                            Stoppen
                          </Button>
                        )}
                        {product.status === 'ARCHIVED' && (
                          <Button
                            busy={busy === `herstellen-${product.id}`}
                            onClick={() =>
                              void run(`herstellen-${product.id}`, () =>
                                api
                                  .post(`/api/products/${product.id}/herstellen`)
                                  .then(() => 'Product teruggezet als concept.'),
                              )
                            }
                          >
                            Terugzetten
                          </Button>
                        )}
                        <Button
                          onClick={() => void navigate(`/bronconfigurator?product=${product.id}`)}
                        >
                          Bronnen
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {zichtbareProducten.length === 0 && (
              <p className={ui.empty}>Geen producten met deze status.</p>
            )}
          </div>
        )}
      </Card>

      <Card
        title="Kiezen uit de XML-feed"
        description="De feed bevat het volledige assortiment. Vink aan wat u wilt monitoren; die artikelen worden als concept aangemaakt."
        actions={
          <Button
            variant="secondary"
            busy={busy === 'catalogus'}
            onClick={() => void haalCatalogus()}
          >
            {catalog ? 'Opnieuw ophalen' : 'Feed ophalen'}
          </Button>
        }
      >
        {!catalog ? (
          <p className={ui.hint}>
            De feed wordt alleen op verzoek opgehaald — dat duurt een paar seconden. Is er nog geen
            feed ingesteld, richt die dan eerst in bij Instellingen.
          </p>
        ) : (
          <>
            <p className={ui.hint}>
              {catalog.totalInFeed} artikelen in de feed
              {catalog.problems > 0 && `, waarvan ${catalog.problems} met een onleesbare prijs`}.
            </p>

            <div className={styles.toolbar}>
              <input
                className={styles.search}
                type="search"
                placeholder="Zoek op naam of artikelnummer"
                value={zoek}
                onChange={(e) => setZoek(e.target.value)}
                aria-label="Zoeken in de feed"
              />
              <Button
                variant="primary"
                busy={busy === 'aanmaken'}
                disabled={gekozen.size === 0}
                onClick={() => void maakAan()}
              >
                {gekozen.size === 0 ? 'Niets geselecteerd' : `${gekozen.size} product(en) aanmaken`}
              </Button>
            </div>

            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th className={styles.checkCol} />
                    <th>Artikel</th>
                    <th>Artikelnummer</th>
                    <th>Kleur</th>
                    <th className={ui.numeric}>Eigen prijs</th>
                    <th>Lengte bevestigen</th>
                  </tr>
                </thead>
                <tbody>
                  {zichtbaar.slice(0, 300).map((entry) => {
                    const bestaat = entry.productId !== null;
                    const aangevinkt = gekozen.has(entry.sku);
                    return (
                      <tr key={entry.sku} className={bestaat ? styles.existingRow : undefined}>
                        <td>
                          <input
                            type="checkbox"
                            checked={aangevinkt}
                            disabled={bestaat}
                            aria-label={`${entry.name ?? entry.sku} selecteren`}
                            onChange={(e) =>
                              setGekozen((huidig) => {
                                const next = new Map(huidig);
                                if (e.target.checked) {
                                  next.set(entry.sku, entry.lengteSuggestie ?? '');
                                } else {
                                  next.delete(entry.sku);
                                }
                                return next;
                              })
                            }
                          />
                        </td>
                        <td>{entry.name ?? '—'}</td>
                        <td>{entry.sku}</td>
                        <td>{entry.kleur ?? '—'}</td>
                        <td className={ui.numeric}>{formatEuro(entry.priceCents)}</td>
                        <td>
                          {bestaat ? (
                            <Chip tone="muted">
                              staat al in de monitor
                              {entry.productStatus === 'ARCHIVED' && ' (gearchiveerd)'}
                            </Chip>
                          ) : (
                            <div className={styles.kleurCel}>
                              <input
                                className={styles.lengthInput}
                                value={gekozen.get(entry.sku) ?? entry.lengteSuggestie ?? ''}
                                placeholder="bv. 600 cm"
                                disabled={!aangevinkt}
                                aria-label={`Lengte voor ${entry.name ?? entry.sku}`}
                                onChange={(e) =>
                                  setGekozen((huidig) =>
                                    new Map(huidig).set(entry.sku, e.target.value),
                                  )
                                }
                              />
                              {entry.lengteSuggestie && (
                                <Chip tone={entry.lengteUitFeed ? 'success' : 'muted'}>
                                  {entry.lengteUitFeed ? 'uit de feed' : 'uit de naam'}
                                </Chip>
                              )}
                              {/*
                                Levert de feed de eigen productpagina, dan wordt die meteen de
                                terugvalbron. Dat is hier zichtbaar omdat een ontbrekende link
                                anders niet te onderscheiden is van een link die niet is gelezen.
                              */}
                              {entry.webshopUrl && <Chip tone="success">eigen pagina</Chip>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {zichtbaar.length > 300 && (
              <p className={ui.hint}>
                Er worden 300 van de {zichtbaar.length} regels getoond. Zoek om de lijst te
                beperken.
              </p>
            )}

            <Notice tone="info">
              Levert de feed een lengteveld, dan wordt die overgenomen — herkenbaar aan &ldquo;uit
              de feed&rdquo;. Staat er &ldquo;uit de naam&rdquo;, dan is de lengte uit de
              productnaam geraden en is controleren nodig: zonder kloppende lengte kan de
              variantcontrole een concurrentprijs niet aan dezelfde uitvoering koppelen. Kleur wordt
              overgenomen maar telt pas mee als u hem hierboven op kritisch zet. Staat er ook een
              eigen productpagina in de feed, dan wordt die meteen als terugvalbron vastgelegd —
              herkenbaar aan &ldquo;eigen pagina&rdquo;.
            </Notice>
          </>
        )}
      </Card>
    </div>
  );
}
